// ============================================================================
// BusinessEntityCard — grille cartes metier (GEST CARTES METIER, TEMPS 2)
// ============================================================================
// Shell partage equipes + affichages : nom/titre + meta + chip + drill-in.
// Composant LOCAL gest — candidat @gs/ui si un 3e consommateur hors-gest emerge.
// Regle #4 : zero emoji.

import { type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { GS_COMPONENTS } from '@gs/ui';

export interface BusinessEntityCardProps {
  title: ReactNode;
  /** Classes optionnelles sur le titre (hierarchie par consommateur). */
  titleClassName?: string;
  subtitle?: ReactNode;
  badge?: ReactNode;
  headerClassName?: string;
  /** Actions en-tete (ex. edition admin) — hors zone cliquable du drill-in. */
  headerActions?: ReactNode;
  /** Route native : preserve ouverture nouvel onglet / Ctrl+clic. */
  to?: string;
  /** Compatibilite des consommateurs non encore routes par un lien natif. */
  onActivate?: () => void;
  /** Libelle aria pour le drill-in (clic + clavier). */
  activateLabel: string;
  children?: ReactNode;
  meta?: ReactNode;
}

export function BusinessEntityCard({
  title,
  titleClassName,
  subtitle,
  badge,
  headerClassName,
  headerActions,
  to,
  onActivate,
  activateLabel,
  children,
  meta,
}: BusinessEntityCardProps) {
  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (onActivate && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onActivate();
    }
  }

  const titleClasses = titleClassName ?? 'text-body font-semibold text-slate-900 dark:text-slate-100';
  const headerClasses = [
    'flex items-start justify-between gap-2',
    headerActions ? 'pr-10' : '',
    headerClassName ?? '',
  ].filter(Boolean).join(' ');

  const card = (
    <article
      role={to ? undefined : 'button'}
      tabIndex={to ? undefined : 0}
      aria-label={to ? undefined : activateLabel}
      onClick={to ? undefined : onActivate}
      onKeyDown={to ? undefined : onKeyDown}
      className={`${GS_COMPONENTS.card} flex h-full min-h-11 cursor-pointer flex-col gap-4 p-4 touch-manipulation transition-shadow group-hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500`}
    >
      <header className={headerClasses}>
        <div className="min-w-0 flex-1">
          <div className={titleClasses}>{title}</div>
          {subtitle != null ? (
            <p className="mt-0.5 truncate text-meta text-slate-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-start gap-1">
          {badge != null ? <div>{badge}</div> : null}
        </div>
      </header>
      {children != null ? <div className="flex flex-1 flex-col gap-3">{children}</div> : null}
      {meta != null ? <div className="mt-auto">{meta}</div> : null}
    </article>
  );

  return (
    <div className="group relative h-full">
      {to ? (
        <Link
          to={to}
          aria-label={activateLabel}
          className="block h-full rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
        >
          {card}
        </Link>
      ) : card}
      {headerActions != null ? (
        <div className="absolute right-3 top-2.5 z-10">{headerActions}</div>
      ) : null}
    </div>
  );
}

export function BusinessEntityCardMetaRow({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 sm:grid-cols-3 dark:border-slate-700">
      {children}
    </div>
  );
}

export interface BusinessEntityCardMetaItemProps {
  label: string;
  children: ReactNode;
  className?: string;
}

export function BusinessEntityCardMetaItem({
  label,
  children,
  className = '',
}: BusinessEntityCardMetaItemProps) {
  return (
    <div className={`flex min-w-0 flex-col gap-0.5 ${className}`.trim()}>
      <p className="text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {label}
      </p>
      <div className="text-meta font-semibold tabular-nums text-slate-700 dark:text-slate-200">
        {children}
      </div>
    </div>
  );
}
